import { useState, useCallback, useRef, useEffect } from 'react';
import { Note } from '../types/notes';
import { useNoteAutosave } from './useNoteAutosave';
import { NoteAutosaveStatus } from '../utils/noteAutosaveStatus';
import { NoteDraft } from '../utils/noteDraft';
import { useCacheStore } from './data/useCacheStore';
import { CACHE_UPDATED_EVENT } from './data/useCacheFirstQuery';
import { shouldReconcileNote } from '../utils/shouldReconcileNote';

export interface NoteEditorState {
  title: string;
  content: string;
  status: NoteAutosaveStatus;
  /** A recovered local draft awaiting a Restore/Discard decision. */
  pendingDraft: NoteDraft | null;
  /**
   * Remote content to push into the live editor on a cross-instance reconcile.
   * `undefined` except immediately after a successful reconcile — the view
   * passes it to RichTextEditor's `externalContent`. It changes ONLY on a real
   * reconcile (never on local typing), so the editor effect fires only then.
   */
  externalContent: string | undefined;
}

export interface NoteEditorActions {
  setTitle: (title: string) => void;
  setContent: (html: string) => void;
  restoreDraft: () => void;
  discardDraft: () => void;
}

/**
 * Model for the full-body note editor. Owns the live title/content, tracks
 * dirtiness against the persisted note, and delegates persistence to
 * useNoteAutosave. No Supabase calls here — the autosave controller / mutations
 * own that.
 */
export function useNoteEditorModel(
  note: Note,
): NoteEditorState & NoteEditorActions {
  // This model is mounted fresh per note (NoteEditorBody has key={note_id}),
  // so state initializes directly from the note prop.
  const [title, setTitleState] = useState(note.note_title);
  const [content, setContentState] = useState(note.note_content);

  // Dirtiness is tracked by an explicit flag, NOT by string comparison.
  // TipTap re-normalizes HTML on load (code blocks, colored spans, etc.), so the
  // editor's serialized output legitimately differs from the stored string even
  // when the user hasn't typed — comparing strings would loop forever
  // (save → normalized emit → "dirty" → save …). The flag only trips on real
  // user edits and clears on a confirmed save.
  const [dirty, setDirty] = useState(false);

  // Cross-instance live-reconcile state (notes sync). We deliberately do this in
  // the model (keyed to the open note_id, reading cache directly) rather than
  // refreshing `selectedNote` in CategoryContext: it keeps the fix inside the
  // editor concern and avoids re-rendering unrelated breadcrumb/tree consumers
  // of selectedNote.
  const cacheStore = useCacheStore();

  // Own-echo / last-known-remote marker: the content the editor currently
  // reflects (last loaded, last reconciled, or last value WE saved). A remote
  // cache event whose content equals this marker is either "no change" or our
  // own save echo — either way, skip. This is a stored marker, NOT the editor's
  // live re-normalized serialization (which would oscillate).
  const lastSyncedContentRef = useRef(note.note_content);
  // Mirror of the latest content state so onSaved can read what was just saved
  // without re-subscribing the autosave controller.
  const contentRef = useRef(content);
  contentRef.current = content;
  // The value the view pushes into RichTextEditor on a reconcile.
  const [externalContent, setExternalContent] = useState<string | undefined>(
    undefined,
  );

  const { status, pendingDraft, restoreDraft, discardDraft } = useNoteAutosave({
    noteId: note.note_id,
    serverLastModified: note.last_modified,
    current: { note_title: title, note_content: content },
    dirty,
    // Clear dirtiness deterministically the moment a save succeeds, rather than
    // reacting to status (which races: there's a render where saving just
    // finished but dirty is still true, re-triggering the save loop). Also mark
    // what we just wrote as the last-synced content so the cache echo from our
    // own save is recognized as own-echo (shouldReconcileNote → false).
    onSaved: () => {
      setDirty(false);
      lastSyncedContentRef.current = contentRef.current;
    },
  });

  // Reconcile the open editor against the live cache on every cache update.
  useEffect(() => {
    const handleCacheUpdate = () => {
      const cached = cacheStore
        .getNotes()
        ?.find((n) => n.note_id === note.note_id);
      // Note gone from cache (deleted/trashed remotely) — handled by the tree /
      // selection layer, out of scope here.
      const remoteContent = cached?.note_content;

      const reconcile = shouldReconcileNote({
        dirty,
        remoteContent,
        lastSyncedContent: lastSyncedContentRef.current,
      });

      if (reconcile && remoteContent !== undefined) {
        console.log(`Update detected — syncing "${note.note_title}"`);
        // Raw state setters so dirtiness is untouched: keep `content` (fed to
        // autosave) and `externalContent` (fed to the editor) consistent so a
        // subsequent local edit diffs from the reconciled baseline.
        setContentState(remoteContent);
        setExternalContent(remoteContent);
        lastSyncedContentRef.current = remoteContent;
      }
    };

    window.addEventListener(CACHE_UPDATED_EVENT, handleCacheUpdate);
    return () => {
      window.removeEventListener(CACHE_UPDATED_EVENT, handleCacheUpdate);
    };
  }, [cacheStore, note.note_id, dirty]);

  const setTitle = useCallback((t: string) => {
    setTitleState(t);
    setDirty(true);
  }, []);

  // Every onChange from the editor is a genuine content change: TipTap does not
  // fire onUpdate for the initial content load, only for subsequent edits
  // (typing, deleting a node, paste, formatting). So any emit = dirty.
  const setContent = useCallback((html: string) => {
    setContentState(html);
    setDirty(true);
  }, []);

  const handleRestoreDraft = useCallback(() => {
    const draft = restoreDraft();
    if (draft) {
      setTitleState(draft.note_title);
      setContentState(draft.note_content);
      setDirty(true);
    }
  }, [restoreDraft]);

  const handleDiscardDraft = useCallback(() => {
    discardDraft();
  }, [discardDraft]);

  return {
    title,
    content,
    status,
    pendingDraft,
    externalContent,
    setTitle,
    setContent,
    restoreDraft: handleRestoreDraft,
    discardDraft: handleDiscardDraft,
  };
}
